import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { reportsAPI } from '../services/api';
import Modal from '../components/common/Modal';
import { Loading, EmptyState } from '../components/common';
import { formatDate, formatCurrency } from '../utils/formatters';
import { REPORT_TYPES, REPORT_STATUSES, CURRENT_YEAR, QUARTERS, YEARS, YEARS_ } from '../utils/constants';
import toast from 'react-hot-toast';
import { DocumentChartBarIcon, PlusIcon, EyeIcon, PencilIcon } from '@heroicons/react/24/outline';

const Reports = () => {
  const { user } = useSelector((state) => state.auth);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [filters, setFilters] = useState({ year: CURRENT_YEAR, quarter: '', status: '' });
  
  const [formData, setFormData] = useState({
    title: '', report_type: 'quarterly', period_year: CURRENT_YEAR, period_quarter: 1,
    total_errors: 0, total_amount: 0, prevented_errors: 0, prevented_amount: 0,
    resolved_errors: 0, resolved_amount: 0, restored_to_budget: 0, notes: '',
  });

  const fetchReports = async () => {
    try {
      const response = await reportsAPI.getAll(filters);
      setReports(response.data.results || response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReports(); }, [filters]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedReport) {
        await reportsAPI.update(selectedReport.id, formData);
        toast.success('Hisobot yangilandi!');
      } else {
        await reportsAPI.create({ ...formData, organization: user?.organization });
        toast.success('Hisobot yaratildi!');
      }
      setShowModal(false);
      fetchReports();
    } catch (error) {
      toast.error('Xatolik yuz berdi');
    }
  };

  const getStatusBadge = (status) => {
    const styles = { draft: 'badge-gray', submitted: 'badge-warning', approved: 'badge-success', rejected: 'badge-danger' };
    return styles[status] || 'badge-gray';
  };

  if (loading) return <Loading />;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Hisobotlar</h1>
          <p className="text-gray-500">Choraklik va yillik hisobotlar</p>
        </div>
        {user?.role !== 'rector' && (
          <button onClick={() => { setSelectedReport(null); setShowModal(true); }} className="btn-primary">
            <PlusIcon className="w-5 h-5" /> Yangi hisobot
          </button>
        )}
      </div>

      <div className="card">
        <div className="flex flex-wrap gap-4">
          <select className="select w-auto" value={filters.year} onChange={(e) => setFilters({ ...filters, year: e.target.value })}>
            {YEARS_.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <select className="select w-auto" value={filters.quarter} onChange={(e) => setFilters({ ...filters, quarter: e.target.value })}>
            <option value="">Barcha choraklar</option>
            {QUARTERS.map(q => <option key={q.value} value={q.value}>{q.label}</option>)}
          </select>
          <select className="select w-auto" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
            <option value="">Barcha holatlar</option>
            {Object.entries(REPORT_STATUSES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
      </div>

      {reports.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {reports.map((report) => (
            <div key={report.id} className="card">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="font-semibold text-gray-800">{report.title}</h3>
                  <p className="text-sm text-gray-500">{REPORT_TYPES[report.report_type]} - {report.period_year}/{report.period_quarter}-chorak</p>
                </div>
                <span className={`badge ${getStatusBadge(report.status)}`}>{REPORT_STATUSES[report.status]}</span>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-gray-400">Jami xatolar:</span> <span className="font-medium">{report.total_errors}</span></div>
                <div><span className="text-gray-400">Jami summa:</span> <span className="font-medium">{formatCurrency(report.total_amount)}</span></div>
                <div><span className="text-gray-400">Oldi olingan:</span> <span className="font-medium text-green-600">{report.prevented_errors}</span></div>
                <div><span className="text-gray-400">Tiklangan:</span> <span className="font-medium text-blue-600">{formatCurrency(report.restored_to_budget)}</span></div>
              </div>
              <div className="flex gap-2 mt-4 pt-4 border-t">
                <button className="btn-ghost btn-sm"><EyeIcon className="w-4 h-4" /> Ko'rish</button>
                {user?.role !== 'rector' && report.status === 'draft' && (
                  <button onClick={() => { setSelectedReport(report); setFormData(report); setShowModal(true); }} className="btn-ghost btn-sm">
                    <PencilIcon className="w-4 h-4" /> Tahrirlash
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState icon={DocumentChartBarIcon} title="Hisobotlar topilmadi" />
      )}

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={selectedReport ? 'Hisobotni tahrirlash' : 'Yangi hisobot'} size="lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="input-group">
            <label className="label">Sarlavha *</label>
            <input type="text" className="input" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="input-group">
              <label className="label">Turi</label>
              <select className="select" value={formData.report_type} onChange={(e) => setFormData({ ...formData, report_type: e.target.value })}>
                {Object.entries(REPORT_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="input-group">
              <label className="label">Yil</label>
              <input type="number" className="input" value={formData.period_year} onChange={(e) => setFormData({ ...formData, period_year: parseInt(e.target.value) })} />
            </div>
            <div className="input-group">
              <label className="label">Chorak</label>
              <select className="select" value={formData.period_quarter} onChange={(e) => setFormData({ ...formData, period_quarter: parseInt(e.target.value) })}>
                {QUARTERS.map(q => <option key={q.value} value={q.value}>{q.value}-chorak</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="input-group">
              <label className="label">Jami xatolar soni</label>
              <input type="number" className="input" value={formData.total_errors} onChange={(e) => setFormData({ ...formData, total_errors: parseInt(e.target.value) || 0 })} />
            </div>
            <div className="input-group">
              <label className="label">Jami summa</label>
              <input type="number" className="input" value={formData.total_amount} onChange={(e) => setFormData({ ...formData, total_amount: parseFloat(e.target.value) || 0 })} />
            </div>
            <div className="input-group">
              <label className="label">Oldi olingan xatolar</label>
              <input type="number" className="input" value={formData.prevented_errors} onChange={(e) => setFormData({ ...formData, prevented_errors: parseInt(e.target.value) || 0 })} />
            </div>
            <div className="input-group">
              <label className="label">Oldi olingan summa</label>
              <input type="number" className="input" value={formData.prevented_amount} onChange={(e) => setFormData({ ...formData, prevented_amount: parseFloat(e.target.value) || 0 })} />
            </div>
            <div className="input-group">
              <label className="label">Bartaraf etilgan xatolar</label>
              <input type="number" className="input" value={formData.resolved_errors} onChange={(e) => setFormData({ ...formData, resolved_errors: parseInt(e.target.value) || 0 })} />
            </div>
            <div className="input-group">
              <label className="label">Byudjetga tiklangan</label>
              <input type="number" className="input" value={formData.restored_to_budget} onChange={(e) => setFormData({ ...formData, restored_to_budget: parseFloat(e.target.value) || 0 })} />
            </div>
          </div>
          <div className="input-group">
            <label className="label">Izoh</label>
            <textarea className="textarea" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">Bekor qilish</button>
            <button type="submit" className="btn-primary">{selectedReport ? 'Saqlash' : 'Yaratish'}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Reports;
